import React, { useEffect, useState } from "react";

import clsx from "clsx";
import {
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarSearch,
  VisualState,
  useKBar,
  useMatches,
} from "kbar";
import { useDebounce } from "usehooks-ts";

import { useUser } from "@components/user-context";
import { GET_COMMAND_PALETTE_SUGGESTIONS } from "@gql/command-palette";
import { logEvent } from "@internal/analytics";
import {
  type GetCommandPaletteSuggestions,
  type GetCommandPaletteSuggestionsVariables,
} from "@north-beam/nb-client-side";
import Spinner from "@shared/spinner";

import { useCommandPaletteContext } from "../hooks";

import { CommandResults } from "./command-results";

const DOCUMENTATION_PRIORITY = 0;
const API_REFERENCE_PRIORITY = 9;

export const CommandDialog = () => {
  const { user } = useUser();
  const { query } = useKBar();
  const { searchQuery, visualState } = useKBar((state) => ({
    searchQuery: state.searchQuery,
    visualState: state.visualState,
  }));
  const { results } = useMatches();
  const { addUnregisterActionCallback, unregisterActionCallbacks } =
    useCommandPaletteContext();

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (visualState === VisualState.showing) {
      logEvent("Command palette -> onOpen:Open command palette");
    }
  }, [visualState]);

  useEffect(() => {
    query.disable(!user.featureFlags.enableCommandPalette);
  }, [query, user.featureFlags.enableCommandPalette]);

  const debouncedSearchQuery = useDebounce(searchQuery, 250);

  useEffect(() => {
    if (searchQuery.length > 0) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const run = async () => {
      setIsLoading(true);
      const result = await user.apolloClient.query<
        GetCommandPaletteSuggestions,
        GetCommandPaletteSuggestionsVariables
      >({
        query: GET_COMMAND_PALETTE_SUGGESTIONS,
        variables: { query: debouncedSearchQuery },
      });

      const actions = result.data.me.commandPaletteSuggestions.map((row) => {
        let priority = DOCUMENTATION_PRIORITY;
        let icon = <i className="fa-regular fa-sharp fa-book-open-cover"></i>;

        if (row.section === "API Reference") {
          priority = API_REFERENCE_PRIORITY;
          icon = <i className="fa-regular fa-sharp fa-code"></i>;
        }

        return {
          id: row.id,
          name: row.title,
          subtitle: row.subtitle,
          section: { name: row.section, priority },
          icon,
          keywords: debouncedSearchQuery,
          perform: () => {
            logEvent(`Command palette -> onSelect:Select ${row.section}`, {
              action: row.url,
            });

            window.open(row.url, "_blank", "noopener,noreferrer");
          },
          priority,
          isExternalLink: true,
          apiMethod: row.apiMethod,
        };
      });

      unregisterActionCallbacks();
      const unregister = query.registerActions(actions);
      addUnregisterActionCallback(unregister);
      setIsLoading(false);
    };

    if (
      visualState === VisualState.animatingIn ||
      visualState === VisualState.showing
    ) {
      run();
    }
  }, [
    user,
    debouncedSearchQuery,
    query,
    visualState,
    addUnregisterActionCallback,
    unregisterActionCallbacks,
  ]);

  return (
    <KBarPortal>
      <KBarPositioner className="z-[9998]">
        <KBarAnimator
          className={clsx(
            "max-w-[840px] h-[523px] w-full bg-v2-primary rounded-lg overflow-hidden shadow-lg border border-v2-tertiary",
          )}
        >
          <div className="relative">
            {isLoading ? (
              <div className="flex items-center justify-center py-4 absolute top-0 left-[16px]">
                <Spinner size="sm" colorClassname="text-v2-action" />
              </div>
            ) : (
              <i
                className={clsx(
                  "fa-regular fa-sharp fa-magnifying-glass absolute top-[16px] left-[16px] text-v2-secondary",
                )}
              ></i>
            )}
            <KBarSearch
              className={clsx(
                "text-base w-full py-3 pr-4 pl-10 border-0 outline-0 text-v2-primary bg-v2-primary",
              )}
              defaultPlaceholder="Type something to start search..."
            />
          </div>
          <CommandResults />
          {results.length === 0 && !isLoading && (
            <div className="py-3 px-4 text-sm text-gray text-center pt-[200px]">
              No results found
            </div>
          )}
        </KBarAnimator>
      </KBarPositioner>
    </KBarPortal>
  );
};
